/**
 * The builder's logo for anything printed — sign-on posters and PDF exports.
 *
 * Set once from the signed-in user rather than threaded through every export
 * call, and loaded lazily: nobody downloads the image until they print.
 */

export interface LoadedLogo {
  image: HTMLImageElement;
  dataUrl: string;
  width: number;
  height: number;
}

/**
 * The packaged artwork, printed when the company has not uploaded its own.
 *
 * The ink version rather than the white one: everything this draws on is paper.
 */
const PACKAGED_MARK = '/brand/paracon-wordmark.png';

let currentUrl: string | null = null;
let cache: { url: string; promise: Promise<LoadedLogo | null> } | null = null;

export function setExportLogo(url: string | null | undefined) {
  currentUrl = url ?? null;
}

/**
 * The logo's URL, for an `<img>`.
 *
 * `loadExportLogo` decodes and re-encodes through a canvas because jsPDF will
 * not take anything else. An HTML page only needs the address, and going
 * through the canvas would cost a repaint for nothing.
 */
export function exportLogoUrl(): string {
  return currentUrl || PACKAGED_MARK;
}

/** The logo, ready to draw — or null when it cannot load at all. A missing logo never stops a print. */
export function loadExportLogo(): Promise<LoadedLogo | null> {
  const url = currentUrl || PACKAGED_MARK;
  if (cache?.url === url) return cache.promise;

  const promise = (async () => {
    try {
      const image = new Image();
      image.src = url;
      await image.decode();

      // Re-encode as PNG so jsPDF takes it whatever the upload format was.
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext('2d')?.drawImage(image, 0, 0);

      return { image, dataUrl: canvas.toDataURL('image/png'), width: image.naturalWidth, height: image.naturalHeight };
    } catch {
      return null;
    }
  })();

  cache = { url, promise };
  return promise;
}

/** Scale a logo to fit a box, keeping its shape. */
export function fitLogo(logo: { width: number; height: number }, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / logo.width, maxHeight / logo.height);
  return { width: logo.width * scale, height: logo.height * scale };
}

/**
 * The letterhead under the logo: the legal name, the ABN and the registered
 * address, exactly as the client's own template prints them.
 *
 * Set from the signed-in user beside the logo, for the same reason — an export
 * can be triggered from any screen, and threading the company through every
 * call site would mean every new export remembering to do it.
 */
export interface ExportBrand {
  legalName: string | null;
  abn: string | null;
  address: string | null;
}

let brand: ExportBrand = { legalName: null, abn: null, address: null };

export function setExportBrand(next: Partial<ExportBrand>) {
  brand = { legalName: null, abn: null, address: null, ...next };
}

export function getExportBrand(): ExportBrand {
  return brand;
}

/** The identity line: "Paracon Group Pty Ltd    ABN 42 166 362 625". */
export function brandIdentityLine(): string | null {
  const parts = [brand.legalName, brand.abn ? `ABN ${brand.abn}` : null].filter(Boolean);

  return parts.length > 0 ? parts.join('    ') : null;
}

/**
 * The bit of jsPDF a letterhead needs. Typed here so a caller does not have to
 * import jsPDF's types to draw one, and so the 390kB stays lazily loaded.
 */
interface PdfLike {
  setFont: (family: string, style?: string) => unknown;
  setFontSize: (size: number) => unknown;
  setTextColor: (r: number, g: number, b: number) => unknown;
  setDrawColor: (r: number, g: number, b: number) => unknown;
  setLineWidth: (width: number) => unknown;
  text: (text: string, x: number, y: number, options?: { align?: 'left' | 'center' | 'right' | 'justify' }) => unknown;
  line: (x1: number, y1: number, x2: number, y2: number) => unknown;
  addImage: (
    data: string,
    format: string,
    x: number,
    y: number,
    width: number,
    height: number,
    alias?: undefined,
    compression?: 'NONE' | 'FAST' | 'MEDIUM' | 'SLOW',
  ) => unknown;
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
}

const INK: [number, number, number] = [26, 32, 44];
const MUTED: [number, number, number] = [113, 128, 150];
const RULE: [number, number, number] = [226, 232, 240];

/**
 * The company's letterhead at the top of a document, and the title under it.
 *
 * Every PDF the product makes wears the same head: the mark, the legal name
 * with the ABN, the registered address, a rule, then what the document is and
 * when it was produced. A client receiving a site diary and a tender letter in
 * the same week should not be able to tell they came from different screens.
 *
 * Returns the y to carry on from.
 */
export async function drawLetterhead(
  doc: PdfLike,
  options: { title: string; subtitle?: string | null; meta?: string | null; margin?: number },
): Promise<number> {
  const margin = options.margin ?? 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const logo = await loadExportLogo();
  const brand = getExportBrand();
  const identity = brandIdentityLine();

  let y = margin;

  if (logo) {
    const size = fitLogo(logo, 132, 30);
    doc.addImage(logo.dataUrl, 'PNG', margin, y, size.width, size.height, undefined, 'FAST');
    y += size.height + 8;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);

  for (const line of [identity, brand.address].filter((entry): entry is string => Boolean(entry))) {
    doc.text(line, margin, y + 6);
    y += 11;
  }

  y += 6;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.7);
  doc.line(margin, y, pageWidth - margin, y);
  y += 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...INK);
  doc.text(options.title, margin, y);

  const under = [options.subtitle, options.meta].filter((entry): entry is string => Boolean(entry));

  if (under.length > 0) {
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text(under.join('  ·  '), margin, y);
  }

  return y + 18;
}

/**
 * The foot of every page: who produced it, when, and where you are in it.
 *
 * A page torn out of a twelve-page register should still say what it is part
 * of — which is the whole reason a footer carries the company name rather than
 * only a number.
 */
export function drawFooter(doc: PdfLike, options: { page: number; pages?: number; margin?: number }): void {
  const margin = options.margin ?? 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const brand = getExportBrand();

  const generated = new Date().toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.5);
  doc.line(margin, pageHeight - margin + 4, pageWidth - margin, pageHeight - margin + 4);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text(`${brand.legalName ?? 'OneParacon'}  ·  Generated ${generated}`, margin, pageHeight - margin + 16);
  doc.text(
    options.pages ? `Page ${options.page} of ${options.pages}` : `Page ${options.page}`,
    pageWidth - margin,
    pageHeight - margin + 16,
    { align: 'right' },
  );
}
